import React, { FC, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Card } from '@altermaind/web/ui';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getContentDetailsCall } from '@/training/hardSkills/state';
import { CardsContent } from '../CardsContent';
import { CardsHeader } from '../CardsHeader';

type BaseProps = {
  id: string;
  thumbnailUrl?: string;
  durationInMinutes?: number;
  rating?: number;
  category?: string;
  title?: string;
  description: string;
  inProgress?: boolean;
  isFavorite?: boolean;
  isCompleted?: boolean;
  isSkillToBoost?: boolean;
};

type Props<Loading extends boolean = boolean> = Loading extends true
  ? Partial<BaseProps> & { loading: true }
  : BaseProps & { loading?: false };

export const Video: FC<Props> = ({
  id,
  thumbnailUrl,
  durationInMinutes,
  rating,
  category,
  title,
  description,
  loading = false,
  inProgress = false,
  isFavorite = false,
  isCompleted = false,
  isSkillToBoost = false,
}) => {
  const t = useTranslations('trainingSoftSkills.cards.VIDEO');
  const { loading: loadingGetContentDetails } = getContentDetailsCall.hook();
  const { openDialog: openDialogVideoStarter } = useDialog('videoStarter');

  const onVideoStart = useCallback(() => {
    openDialogVideoStarter({
      videoId: id,
      isSkillToBoost,
    });
  }, [id, isSkillToBoost, openDialogVideoStarter]);

  const textButton = useMemo(() => {
    if (inProgress) {
      return t('resume');
    }
    if (isCompleted) {
      return t('restart');
    }
    return t('start');
  }, [inProgress, isCompleted, t]);

  const iconButton = useMemo(() => {
    if (inProgress) {
      return 'emptyPlay';
    }
    if (isCompleted) {
      return 'refresh';
    }
    return 'emptyPlay';
  }, [inProgress, isCompleted]);

  return (
    <Card
      width='100%'
      padding='none'
      justifyContent='space-between'
      display='flex'
      flexDirection='column'
      height='100%'
    >
      <CardsHeader
        contentId={id as string}
        rating={rating}
        mainInfoContent={
          durationInMinutes
            ? {
                text: t('duration', {
                  time: dayjs.duration(durationInMinutes ?? 0, 'minutes').asMinutes(),
                }),
                iconName: 'emptyPlay',
              }
            : undefined
        }
        thumbnailUrl={thumbnailUrl}
        loading={loading}
        isFavorite={isFavorite}
        isCompleted={isCompleted}
        inProgress={inProgress}
        isSkillToBoost={isSkillToBoost}
      />
      <CardsContent
        button={{
          iconName: iconButton,
          text: textButton,
          onClick: onVideoStart,
          loading: loadingGetContentDetails,
        }}
        category={category}
        title={title}
        description={description || ''}
        loading={loading}
      />
    </Card>
  );
};
